// Apolo IDE — Turn Role Router (v1: só downgrade, nunca upgrade).
//
// Módulo PURO: ZERO imports. O registro de modelos é INJETADO como um array
// simples (`availableModels`) — o domínio nunca importa o modelRegistry, para
// permanecer testável e desacoplado.
//
// Política conservadora: só rebaixa o modelo do turno (para um mais barato) e
// SOMENTE quando o usuário está no automático (currentModel === defaultModel).
// Se o usuário fixou um modelo, a soberania dele é respeitada e nada muda.

/**
 * Resolve o modelo a USAR neste turno, ou null para "manter o currentModel".
 * O wiring faz: `effModel = routedId || effModel`.
 *
 * @param {object} args
 * @param {string} [args.modelTier] - intenção do roteador: 'cheap' rebaixa,
 *   'keep'/desconhecido mantém.
 * @param {string} args.currentModel - id do modelo do turno atual.
 * @param {string} args.defaultModel - id do modelo default (automático).
 * @param {Array<{id: string, score: number}>} args.availableModels - modelos
 *   disponíveis; `score` é proxy de preço (MENOR = mais barato).
 * @returns {string|null} id do modelo a usar, ou null para manter o atual.
 */
export function resolveRoutedModel(args) {
  // 1. Guard: entradas malformadas → null (nunca lança).
  if (!args || typeof args !== 'object') return null;
  const { modelTier, currentModel, defaultModel, availableModels } = args;
  if (!Array.isArray(availableModels)) return null;
  if (typeof currentModel !== 'string') return null;
  if (typeof defaultModel !== 'string') return null;

  // 2. Soberania do usuário: se ele fixou um modelo, nunca sobrepor.
  if (currentModel !== defaultModel) return null;

  // 3. v1 só rebaixa: 'keep'/desconhecido → manter o atual.
  if (modelTier !== 'cheap') return null;

  // 4. Localizar o score do modelo atual; sem ele não dá pra comparar.
  const cur = availableModels.find((m) => m && m.id === currentModel);
  if (!cur || typeof cur.score !== 'number') return null;

  // 5. Candidatos mais baratos que o atual, com id string válido.
  // score > 0: modelos GRÁTIS (ex.: OpenRouter `:free`, pricing 0/0 → score 0)
  // são instáveis — frequentemente ficam SEM provider ativo e o upstream devolve
  // "No allowed providers are available for the selected model". O downgrade
  // AUTOMÁTICO nunca deve cair num desses; a seleção MANUAL do usuário (soberania,
  // passo 2) continua valendo. Sem este guard, todo turno chat/análise no
  // automático era roteado pro modelo free e quebrava.
  const candidates = availableModels.filter(
    (m) =>
      m &&
      typeof m.id === 'string' &&
      m.id !== currentModel &&
      typeof m.score === 'number' &&
      m.score > 0 &&
      m.score < cur.score,
  );
  if (candidates.length === 0) return null;

  // 6. O mais barato (menor score); empate → id lexicograficamente primeiro.
  let best = candidates[0];
  for (const m of candidates) {
    if (m.score < best.score || (m.score === best.score && m.id < best.id)) {
      best = m;
    }
  }
  return best.id;
}
